// Generic modal. Content is rendered by the caller; onMount wires its own events.
import { html, raw } from '../lib/dom';
import { icon } from './icons';
import { usesDesignSystem } from '../features/design-v2/rollout';
import { parseHash } from '../lib/router';
import * as store from '../store/ui';

export function openModal({ title, body = '', foot = '', wide = false, onMount, bare = false, label, returnFocus }: any) {
  const scrim = document.createElement('div');
  scrim.className = 'scrim';
  const route = parseHash();
  if (usesDesignSystem(route)) {
    scrim.classList.add('nodex-v2', 'v2-dialog');
    scrim.dataset.theme = store.get().theme === 'dark' ? 'dark' : 'light';
  }
  scrim.innerHTML = String(bare
    ? html`<div class="modal ${wide ? 'wide' : ''}" role="dialog" aria-modal="true" aria-label="${label || title || 'Dialog'}">${body}</div>`
    : html`<div class="modal ${wide ? 'wide' : ''}" role="dialog" aria-modal="true" aria-label="${title}">
        <div class="modal-head"><h2 style="flex:1">${title}</h2><button class="icon-btn" data-close aria-label="Close">${icon('close')}</button></div>
        <div class="modal-body">${body}</div>
        ${foot ? html`<div class="modal-foot">${foot}</div>` : raw('')}
      </div>`);
  const prevFocus = document.activeElement as HTMLElement;
  let modal: HTMLElement | null = null;
  let closed = false;
  const close = () => {
    if (closed) return;
    closed = true;
    scrim.remove();
    document.removeEventListener('keydown', onKey, true);
    const focusTarget = typeof returnFocus === 'function' ? returnFocus() : returnFocus || prevFocus;
    if (focusTarget?.isConnected) focusTarget.focus();
  };
  const onKey = (e?: KeyboardEvent) => {
    if (e.key === 'Escape') { e.stopPropagation(); close(); return; }
    if (e.key !== 'Tab' || !modal) return;
    const focusable = [...modal.querySelectorAll<HTMLElement>(
      'a[href],button:not([disabled]),input:not([type="hidden"]):not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])',
    )].filter(element => !element.closest('[hidden],[aria-hidden="true"]')
      && element.getClientRects().length > 0
      && getComputedStyle(element).visibility !== 'hidden');
    if (!focusable.length) { e.preventDefault(); modal.focus(); return; }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (!modal.contains(active)) {
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
    } else if (e.shiftKey && active === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault(); first.focus();
    }
  };
  scrim.addEventListener('mousedown', (e?: any) => { if (e.target === scrim) close(); });
  scrim.addEventListener('click', (e?: any) => { if (e.target.closest('[data-close]')) close(); });
  document.body.append(scrim);
  modal = scrim.querySelector('.modal');
  document.addEventListener('keydown', onKey, true);
  onMount?.(modal, close);
  (modal.querySelector<HTMLElement>('[autofocus]') || modal.querySelector<HTMLElement>('input, button:not([data-close])'))?.focus();
  return close;
}

export function confirmModal({ title, text, confirmLabel = 'Confirm', danger = false, onConfirm }: any) {
  return openModal({
    title,
    body: html`<p>${text}</p><p class="banner error" role="alert" data-confirm-error hidden></p>`,
    foot: html`<button class="btn ghost" data-close>Cancel</button><button class="btn ${danger ? 'danger' : 'primary'}" data-confirm>${confirmLabel}</button>`,
    onMount: (m?: any, close?: any) => m.querySelector('[data-confirm]').addEventListener('click', async (event?: any) => {
      const button = event.currentTarget as HTMLButtonElement;
      if (button.disabled) return;
      const error = m.querySelector('[data-confirm-error]');
      button.disabled = true;
      error.hidden = true;
      try {
        await onConfirm?.();
        close();
      } catch (cause: any) {
        error.textContent = cause?.message || 'The action could not be completed.';
        error.hidden = false;
      } finally { button.disabled = false; }
    }),
  });
}
